import { createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';
import { isDefined } from '@sgme/fp';

import { mapWithKey, sortByBrdAndCurrencyPair } from '@/store/bulkOrder/helpers/utils.ts';
import type { OrderError } from '@/store/bulkOrder/types.ts';
import { createRecord, groupBy } from '@/utils/array.ts';

import {
  BulkOrderStatus,
  type BulkStateData,
  type FixingBulkOrder,
  type FixingBulkOrderProperties,
  type FixingByBdrid,
  type FixingOrderError,
  FixingOrderFieldName,
  type FxOrderBulksState,
  type StopLossBulkOrder,
  type StopLossBulkOrderProperties,
  type StopLossByBdrid,
  type StopLossOrderError,
  StopLossOrderFieldName,
} from './state';

export const orderBulksSlice = createSlice({
  name: 'orderBulks',
  initialState: {} as FxOrderBulksState,

  reducers: {
    resetBulkOrders: (state: Draft<FxOrderBulksState>, action: PayloadAction<{ tabId: string }>) => {
      state[action.payload.tabId] = {
        stopLoss: { orderIds: [], orderById: {}, orderByBdrId: {} },
        fixing: { orderIds: [], orderById: {}, orderByBdrId: {} },
      };
    },
    // ███████╗████████╗ ██████╗ ██████╗ ██╗      ██████╗ ███████╗███████╗     ██████╗ ██████╗ ██████╗ ███████╗██████╗
    // ██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗██║     ██╔═══██╗██╔════╝██╔════╝    ██╔═══██╗██╔══██╗██╔══██╗██╔════╝██╔══██╗
    // ███████╗   ██║   ██║   ██║██████╔╝██║     ██║   ██║███████╗███████╗    ██║   ██║██████╔╝██║  ██║█████╗  ██████╔╝
    // ╚════██║   ██║   ██║   ██║██╔═══╝ ██║     ██║   ██║╚════██║╚════██║    ██║   ██║██╔══██╗██║  ██║██╔══╝  ██╔══██╗
    // ███████║   ██║   ╚██████╔╝██║     ███████╗╚██████╔╝███████║███████║    ╚██████╔╝██║  ██║██████╔╝███████╗██║  ██║
    // ╚══════╝   ╚═╝    ╚═════╝ ╚═╝     ╚══════╝ ╚═════╝ ╚══════╝╚══════╝     ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚══════╝╚═╝  ╚═╝

    stopLossOrdersLoaded(
      state: Draft<FxOrderBulksState>,
      action: PayloadAction<{ stopLosses: StopLossBulkOrder[]; tabId: string }>,
    ) {
      const allStopLosses = action.payload.stopLosses;
      const orderByBdrId = groupStopLossOrders(allStopLosses);

      const allSortedStopLosses = Object.values(orderByBdrId)
        .flatMap((orderByCurrencyPair) => Object.values(orderByCurrencyPair).flat())
        .sort(sortByBrdAndCurrencyPair);

      state[action.payload.tabId] = {
        ...state[action.payload.tabId],
        stopLoss: {
          orderIds: allSortedStopLosses.map((stopLoss) => stopLoss.properties.id),
          orderById: createRecord(allStopLosses, (stopLoss) => stopLoss.properties.id),
          orderByBdrId: orderByBdrId,
        },
      } as BulkStateData;
    },
    stopLossOrderStatusChanged(
      state: Draft<FxOrderBulksState>,
      action: PayloadAction<{
        orderId: string;
        status: Exclude<BulkOrderStatus, BulkOrderStatus.INVALID>;
        tabId: string;
      }>,
    ) {
      const order = state?.[action.payload.tabId]?.stopLoss.orderById[action.payload.orderId];

      if (order === undefined) {
        return;
      }

      order.properties.status = action.payload.status;
    },
    stopLossOrderInvalid(
      state: Draft<FxOrderBulksState>,
      action: PayloadAction<{ orderId: string; errors: StopLossOrderError[]; tabId: string }>,
    ) {
      const order = state?.[action.payload.tabId]?.stopLoss.orderById[action.payload.orderId];

      if (order === undefined) {
        return;
      }

      order.properties.status = BulkOrderStatus.INVALID;
      order.properties.errors = action.payload.errors.map(transformStopLossError);
    },

    stopLossOrderSuccess(
      state: Draft<FxOrderBulksState>,
      action: PayloadAction<{ orderId: string; omsId: string; tabId: string }>,
    ) {
      const order = state?.[action.payload.tabId]?.stopLoss.orderById[action.payload.orderId];

      if (order === undefined) {
        return;
      }

      order.properties.status = BulkOrderStatus.SUCCESS;
      order.properties.omsId = action.payload.omsId;
    },
    stopLossOrderPropertiesChanged(
      state: Draft<FxOrderBulksState>,
      action: PayloadAction<{ orderId: string; properties: Partial<StopLossBulkOrderProperties>; tabId: string }>,
    ) {
      const order = state?.[action.payload.tabId]?.stopLoss.orderById[action.payload.orderId];
      if (order === undefined) {
        return;
      }

      Object.assign(order.properties, action.payload.properties);
    },
    // ███████╗██╗██╗  ██╗██╗███╗   ██╗ ██████╗      ██████╗ ██████╗ ██████╗ ███████╗██████╗
    // ██╔════╝██║╚██╗██╔╝██║████╗  ██║██╔════╝     ██╔═══██╗██╔══██╗██╔══██╗██╔════╝██╔══██╗
    // █████╗  ██║ ╚███╔╝ ██║██╔██╗ ██║██║  ███╗    ██║   ██║██████╔╝██║  ██║█████╗  ██████╔╝
    // ██╔══╝  ██║ ██╔██╗ ██║██║╚██╗██║██║   ██║    ██║   ██║██╔══██╗██║  ██║██╔══╝  ██╔══██╗
    // ██║     ██║██╔╝ ██╗██║██║ ╚████║╚██████╔╝    ╚██████╔╝██║  ██║██████╔╝███████╗██║  ██║
    // ╚═╝     ╚═╝╚═╝  ╚═╝╚═╝╚═╝  ╚═══╝ ╚═════╝      ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚══════╝╚═╝  ╚═╝

    fixingOrdersLoaded(
      state: Draft<FxOrderBulksState>,
      action: PayloadAction<{ fixings: FixingBulkOrder[]; tabId: string }>,
    ) {
      const allFixings = action.payload.fixings;
      const orderByBdrId = groupFixingOrders(allFixings);

      const allSortedFixings = Object.values(orderByBdrId)
        .flatMap((orderByCurrencyPair) => Object.values(orderByCurrencyPair).flat())
        .sort(sortByBrdAndCurrencyPair);

      state[action.payload.tabId] = {
        ...state[action.payload.tabId],
        fixing: {
          orderIds: allSortedFixings.map((fixing) => fixing.properties.id),
          orderById: createRecord(allFixings, (fixing) => fixing.properties.id),
          orderByBdrId: orderByBdrId,
        },
      } as BulkStateData;
    },

    fixingOrderStatusChanged(
      state: Draft<FxOrderBulksState>,
      action: PayloadAction<{
        orderId: string;
        status: Exclude<BulkOrderStatus, BulkOrderStatus.INVALID>;
        tabId: string;
      }>,
    ) {
      const order = state?.[action.payload.tabId]?.fixing.orderById[action.payload.orderId];

      if (order === undefined) {
        return;
      }

      order.properties.status = action.payload.status;
    },

    fixingOrderInvalid(
      state: Draft<FxOrderBulksState>,
      action: PayloadAction<{ orderId: string; errors: FixingOrderError[]; tabId: string }>,
    ) {
      const order = state?.[action.payload.tabId]?.fixing.orderById[action.payload.orderId];

      if (order === undefined) {
        return;
      }

      order.properties.status = BulkOrderStatus.INVALID;
      order.properties.errors = action.payload.errors.map(transformFixingError);
    },
    fixingOrderSuccess(
      state: Draft<FxOrderBulksState>,
      action: PayloadAction<{ orderId: string; omsId: string; tabId: string }>,
    ) {
      const order = state?.[action.payload.tabId]?.fixing.orderById[action.payload.orderId];

      if (order === undefined) {
        return;
      }

      order.properties.status = BulkOrderStatus.SUCCESS;
      order.properties.omsId = action.payload.omsId;
    },
    fixingOrderPropertiesChanged(
      state: Draft<FxOrderBulksState>,
      action: PayloadAction<{ orderId: string; properties: Partial<FixingBulkOrderProperties>; tabId: string }>,
    ) {
      const order = state?.[action.payload.tabId]?.fixing.orderById[action.payload.orderId];

      if (order === undefined) {
        return;
      }

      Object.assign(order.properties, action.payload.properties);
    },
  },
});

export const {
  fixingOrdersLoaded,
  stopLossOrdersLoaded,
  fixingOrderStatusChanged,
  stopLossOrderStatusChanged,
  fixingOrderInvalid,
  stopLossOrderInvalid,
  stopLossOrderSuccess,
  fixingOrderSuccess,
  fixingOrderPropertiesChanged,
  stopLossOrderPropertiesChanged,
  resetBulkOrders,
} = orderBulksSlice.actions;

const groupStopLossOrders = (allStopLosses: StopLossBulkOrder[]): StopLossByBdrid => {
  const stopLossByBdrid = groupBy(allStopLosses, (stopLoss) => stopLoss.properties.bdrId);

  return mapWithKey(stopLossByBdrid, (group) => groupBy(group, (stopLoss) => stopLoss.properties.currencyPair));
};

const groupFixingOrders = (allFixings: FixingBulkOrder[]): FixingByBdrid => {
  const fixingByBdrid = groupBy(allFixings, (fixing) => fixing.properties.bdrId);

  return mapWithKey(fixingByBdrid, (group) => groupBy(group, (fixing) => fixing.properties.currencyPair));
};

const transformStopLossError = (error: OrderError) => {
  return {
    ...error,
    field: isDefined(error.field) ? STOPLOSS_FIELD_NAME_MAPPING[error.field] : error.field,
  };
};

const transformFixingError = (error: OrderError) => {
  return {
    ...error,
    field: isDefined(error.field) ? FIXING_FIELD_NAME_MAPPING[error.field] : error.field,
  };
};

const STOPLOSS_FIELD_NAME_MAPPING: Record<string, StopLossOrderFieldName> = {
  currencyPair: StopLossOrderFieldName.currencyPair,
  bdrId: StopLossOrderFieldName.bdrId,
  way: StopLossOrderFieldName.way,
  amountInCcy1: StopLossOrderFieldName.notional,
  amountInCcy2: StopLossOrderFieldName.notional,
  limitPrice: StopLossOrderFieldName.limitPrice,
  expiryDay: StopLossOrderFieldName.expiryDate,
  trigger: StopLossOrderFieldName.trigger,
  notificationEmails: StopLossOrderFieldName.notificationEmails,
};

const FIXING_FIELD_NAME_MAPPING: Record<string, FixingOrderFieldName> = {
  currencyPair: FixingOrderFieldName.currencyPair,
  bdrId: FixingOrderFieldName.bdrId,
  way: FixingOrderFieldName.way,
  amountInCcy1: FixingOrderFieldName.notional,
  amountInCcy2: FixingOrderFieldName.notional,
  expiryDay: FixingOrderFieldName.deliveryDate,
  notificationEmails: FixingOrderFieldName.notificationEmails,
  fixingReference: FixingOrderFieldName.fixingReference,
  fixingPlace: FixingOrderFieldName.fixingPlace,
  fixingPriceType: FixingOrderFieldName.fixingPriceType,
  fixingDateUtc: FixingOrderFieldName.fixingDateUtc,
  fixingTime: FixingOrderFieldName.fixingTime,
  fixingMarginType: FixingOrderFieldName.fixingMarginType,
  commercialMargin: FixingOrderFieldName.commercialMargin,
};

export type AllFxOrderBulkActions = typeof orderBulksSlice.actions;
export type FxOrderBulkAction = AllFxOrderBulkActions[keyof AllFxOrderBulkActions];
